import { useCallback, useMemo, useRef, useState } from 'react';
import { useIsShiftPressed } from '@/hooks/use-is-shift-pressed';
import { rangeSelectionHelper } from '@/utils/range-selection';
import { selectionFlags, type SelectionFlags } from './selection-flags';

export type RelationshipsSelection = SelectionFlags & {
  /** Keys of the selected rows, all of them on the current page. */
  selectedKeys: ReadonlySet<string>;
  selectedCount: number;
  selectRow: (key: string, index: number, checked: boolean) => void;
  selectPage: (checked: boolean) => void;
  clear: () => void;
};

type ShiftAnchors = { start: number | null; end: number | null };

/**
 * Row selection for the relationships table, keyed on the entity pair rather
 * than on row identity so a refetch cannot silently drop the selection.
 *
 * There is no cross-page select-all here: paging clears the selection, so the
 * whole set counts as selected only when a single page holds all of it.
 */
export function useRelationshipsSelection(pageKeys: string[], total: number): RelationshipsSelection {
  const [selected, setSelected] = useState<string[]>([]);
  const isShiftPressed = useIsShiftPressed();
  const anchors = useRef<ShiftAnchors>({ start: null, end: null });

  const selectRow = useCallback(
    (key: string, index: number, checked: boolean) => {
      setSelected((current) => {
        const result = rangeSelectionHelper<string>({
          models: pageKeys,
          singleModel: key,
          selectedItems: current,
          index,
          checked,
          totalRecs: total,
          isShiftPressed: isShiftPressed.current === true,
          shiftSelectStart: anchors.current.start,
          shiftSelectEnd: anchors.current.end,
        });
        anchors.current = { start: result.tempShiftSelectStart, end: result.tempShiftSelectEnd };
        return result.tempSelectedItems;
      });
    },
    [isShiftPressed, pageKeys, total],
  );

  // Any selection change that is not a single row leaves no anchor to extend
  // from, so a later shift-click starts a fresh range instead of reaching back
  // to a row that is no longer selected — or to an index on the previous page.
  const selectPage = useCallback(
    (checked: boolean) => {
      anchors.current = { start: null, end: null };
      setSelected(checked ? [...pageKeys] : []);
    },
    [pageKeys],
  );
  const clear = useCallback(() => {
    anchors.current = { start: null, end: null };
    setSelected([]);
  }, []);
  const selectedKeys = useMemo(() => new Set(selected), [selected]);

  return {
    ...selectionFlags(selected.length, pageKeys.length, total),
    selectedKeys,
    selectedCount: selected.length,
    selectRow,
    selectPage,
    clear,
  };
}
